import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Play, Pause, ListPlus, Star, Download, CheckCircle, MoreVertical, Music, Clock } from 'lucide-react';
import { AudioFile } from '../types';
import { getAssetUrl, getFetchOptions } from '../config/environment';
import './NaatCard.css';

interface NaatCardProps {
  naat: AudioFile;
  serialNumber: number;
  isPlaying: boolean;
  isFavorite: boolean;
  onPlay: (naat: AudioFile) => void;
  onPause: () => void;
  onAddToQueue: (naat: AudioFile) => void;
  onToggleFavorite: (naat: AudioFile) => void;
  onDownloadComplete?: (naat: AudioFile, success: boolean) => void;
}

const NaatCard: React.FC<NaatCardProps> = ({
  naat,
  serialNumber,
  isPlaying,
  isFavorite,
  onPlay,
  onPause,
  onAddToQueue,
  onToggleFavorite,
  onDownloadComplete,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      onPause();
    } else {
      onPlay(naat);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          menuButtonRef.current && !menuButtonRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleDownload = async () => {
    if (isDownloading || downloadComplete) return;

    setIsDownloading(true);

    try {
      // Construct the audio file URL using environment configuration
      const audioUrl = getAssetUrl(naat.filePath);

      // Fetch the audio file with proper CORS options
      const response = await fetch(audioUrl, getFetchOptions());
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Format filename: "NaatTitle - ArtistName.mp3"
      const artistName = naat.naatKhawan?.name || 'Unknown Artist';
      const filename = `${naat.title} - ${artistName}.mp3`.replace(/[^a-z0-9\s\-\_\.]/gi, '');
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      // Show success state
      setIsDownloading(false);
      setDownloadComplete(true);

      // Notify parent component
      onDownloadComplete?.(naat, true);

      // Reset after 3 seconds
      setTimeout(() => {
        setDownloadComplete(false);
      }, 3000);

    } catch (error) {
      console.error('Download failed:', error);
      setIsDownloading(false);

      // Notify parent component of failure
      onDownloadComplete?.(naat, false);
    }
  };

  const handleMenuClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
    setShowMenu(false);
  };

  return (
    <div className={`naat-card ${isPlaying ? 'naat-card-playing' : ''}`}>
      <div className="naat-card-main">
        <button
          className={`naat-play-button ${isPlaying ? 'playing' : ''}`}
          onClick={handlePlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <>
              <span className="play-bars">
                <span className="bar"></span>
                <span className="bar"></span>
                <span className="bar"></span>
              </span>
              <Pause size={20} className="play-icon" />
            </>
          ) : (
            <Play size={20} className="play-icon" />
          )}
        </button>

        <div className="naat-info">
          <Link
            to={`/naat/${naat.id}`}
            className="naat-title-link"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="naat-title" title={naat.title}>{naat.title}</h3>
          </Link>

          {naat.naatKhawan && (
            <div className="naat-artist-container">
              <Link
                to={`/naatkhawan/${naat.naatKhawan.id}`}
                className="naat-artist-link"
                onClick={(e) => e.stopPropagation()}
              >
                {naat.naatKhawan.name}
              </Link>
            </div>
          )}

          <div className="naat-meta">
            <span className="meta-item">
              <Music size={12} className="meta-icon" />
              {naat.playCount.toLocaleString()} plays
            </span>
            {naat.duration && (
              <>
                <span className="meta-separator">•</span>
                <span className="meta-item">
                  <Clock size={12} className="meta-icon" />
                  {formatDuration(naat.duration)}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="naat-menu-container">
          <button
            ref={menuButtonRef}
            className="naat-menu-button"
            onClick={(e) => {
              e.stopPropagation();
              if (!showMenu && menuButtonRef.current) {
                const rect = menuButtonRef.current.getBoundingClientRect();
                const menuWidth = 200;
                const menuHeight = 180; // Approximate height

                // Calculate position, ensuring menu stays within viewport
                let top = rect.bottom + 4;
                let left = rect.right - menuWidth;

                // Check if menu would go off bottom of screen
                if (top + menuHeight > window.innerHeight) {
                  top = rect.top - menuHeight - 4;
                }

                // Check if menu would go off left edge
                if (left < 0) {
                  left = rect.left;
                }

                setMenuPosition({ top, left });
              }
              setShowMenu(!showMenu);
            }}
            aria-label="More options"
          >
            <MoreVertical size={20} />
          </button>

          {showMenu && ReactDOM.createPortal(
            <div
              className="naat-menu-dropdown"
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
              ref={menuRef}
            >
              <button
                className="menu-item"
                onClick={(e) => handleMenuClick(e, () => onAddToQueue(naat))}
              >
                <ListPlus size={16} />
                <span>Add to Queue</span>
              </button>

              <button
                className={`menu-item ${isFavorite ? 'is-favorite' : ''}`}
                onClick={(e) => handleMenuClick(e, () => onToggleFavorite(naat))}
              >
                <Star size={16} fill={isFavorite ? 'currentColor' : 'none'} />
                <span>{isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}</span>
              </button>

              <button
                className={`menu-item ${isDownloading ? 'downloading' : ''} ${downloadComplete ? 'download-complete' : ''}`}
                onClick={(e) => handleMenuClick(e, handleDownload)}
                disabled={isDownloading}
              >
                {downloadComplete ? (
                  <>
                    <CheckCircle size={16} />
                    <span>Downloaded!</span>
                  </>
                ) : (
                  <>
                    <Download size={16} className={isDownloading ? 'download-icon-animate' : ''} />
                    <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
                  </>
                )}
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
};

export default NaatCard;